import { ApiError, apiRequest } from "./http";
import {
  AuthSession,
  AuthUser,
  clearSession,
  getDeviceId,
  isAccessValid,
  isRefreshValid,
  loadSession,
  makeSession,
  saveSession,
} from "./tokenStore";

type SendCodeResponse = {
  success: boolean;
  cooldownSec: number;
};

type LoginResponse = {
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
  refreshExpiresIn: number;
  user: AuthUser;
};

type RefreshResponse = {
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
  refreshExpiresIn: number;
};

export type SessionInvalidationReason = "signed-in-elsewhere" | "expired";

export const sessionInvalidatedEventName = "sportarc:session-invalidated";

export type MeResponse = {
  uid: number;
  phoneMasked: string;
  nickname: string;
  avatar: string;
  language?: string;
};

let refreshTask: Promise<AuthSession> | null = null;

function sessionInvalidationReason(error: ApiError): SessionInvalidationReason {
  switch (error.code) {
    case "ERR_TOKEN_EXPIRED":
    case "ERR_REFRESH_TOKEN_REVOKED":
      return "signed-in-elsewhere";
    default:
      return "expired";
  }
}

function invalidateStoredSession(error: ApiError): void {
  const hadSession = loadSession() !== null;
  clearSession();
  if (!hadSession) return;

  window.dispatchEvent(
    new CustomEvent<SessionInvalidationReason>(sessionInvalidatedEventName, {
      detail: sessionInvalidationReason(error),
    }),
  );
}

export function getStoredSession(): AuthSession | null {
  const session = loadSession();
  if (!session) {
    return null;
  }

  if (!isRefreshValid(session)) {
    clearSession();
    return null;
  }

  return session;
}

export async function sendLoginCode(phone: string): Promise<SendCodeResponse> {
  return apiRequest<SendCodeResponse>("auth/send-code", {
    method: "POST",
    body: { phone, scene: "login" },
  });
}

export async function loginWithCode(phone: string, code: string): Promise<AuthSession> {
  const deviceId = getDeviceId();
  const response = await apiRequest<LoginResponse>("auth/login", {
    method: "POST",
    body: {
      phone,
      code,
      device: {
        id: deviceId,
        platform: "teacher-web",
      },
    },
  });
  const session = makeSession({ ...response, deviceId });
  saveSession(session);
  return session;
}

async function refreshSession(session: AuthSession): Promise<AuthSession> {
  const response = await apiRequest<RefreshResponse>("auth/refresh", {
    method: "POST",
    body: { refreshToken: session.refreshToken },
  });
  const nextSession = makeSession({
    ...response,
    user: session.user,
    deviceId: session.deviceId,
  });
  saveSession(nextSession);
  return nextSession;
}

export async function ensureAccessToken(): Promise<string> {
  const session = getStoredSession();
  if (!session) {
    throw new ApiError(401, "请先登录");
  }

  if (isAccessValid(session)) {
    return session.accessToken;
  }

  refreshTask ??= refreshSession(session).finally(() => {
    refreshTask = null;
  });

  try {
    const refreshed = await refreshTask;
    return refreshed.accessToken;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      invalidateStoredSession(error);
    }
    throw error;
  }
}

export async function authenticatedRequest<T>(
  path: string,
  options: Omit<Parameters<typeof apiRequest<T>>[1], "accessToken"> = {},
): Promise<T> {
  const accessToken = await ensureAccessToken();

  try {
    return await apiRequest<T>(path, { ...options, accessToken });
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      invalidateStoredSession(error);
    }
    throw error;
  }
}

export async function fetchMe(): Promise<MeResponse> {
  return authenticatedRequest<MeResponse>("me");
}

export async function logoutCurrentSession(): Promise<void> {
  const session = getStoredSession();
  if (!session) {
    clearSession();
    return;
  }

  let accessToken = session.accessToken;

  if (!isAccessValid(session)) {
    refreshTask ??= refreshSession(session).finally(() => {
      refreshTask = null;
    });

    try {
      accessToken = (await refreshTask).accessToken;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        clearSession();
        return;
      }
      throw error;
    }
  }

  try {
    await apiRequest<void>("auth/logout", {
      method: "POST",
      accessToken,
      body: { revokeRefresh: true },
    });
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      clearSession();
      return;
    }
    throw error;
  }

  clearSession();
}

export function logoutFailureMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 0) {
    return "网络连接失败，尚未退出。请恢复网络后重试。";
  }
  return "退出失败，尚未退出。请稍后重试。";
}

export function logoutLocally(): void {
  clearSession();
}
